import React from 'react';

import { Box, IconButton, Modal } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { Heading } from '../Heading';
import { useKlpStyles } from '../Theme/useKlpStyles';
import type { Theme } from '@material-ui/core';
import type { WithTranslation } from '../TranslationOrChildren';
import type { KlpStyled } from '../Theme';
import { removeKlpStyles } from '../Theme';
import classNames from 'classnames';

// Compound component parts
const KlpModalHeader = ({ children }: { children: React.ReactNode }) => (
    <>{children}</>
);
const KlpModalContent = ({ children }: { children: React.ReactNode }) => (
    <>{children}</>
);
const KlpModalFooter = ({ children }: { children: React.ReactNode }) => (
    <>{children}</>
);

const styles = (theme: Theme) => ({
    modal: {
        width: '90vw',
        maxHeight: '90vh',
        overflow: 'hidden' as const,
        position: 'absolute' as const,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: theme.palette.primary.veryLight,
        boxShadow: theme.shadows[24],
        borderRadius: theme.shape.borderRadius,
        outline: 'none',
        display: 'flex',
        flexDirection: 'column' as const,
        '&:focus': {
            outline: 'none',
        },
        [theme.breakpoints.down('sm')]: {
            width: '100vw',
            maxHeight: '100vh',
            top: 0,
            left: 0,
            transform: 'none',
            border: 'none',
            borderRadius: 0,
        },
    },
    content: {
        padding: theme.spacing(4),
        paddingTop: 0,
        marginTop: theme.spacing(2),
        maxWidth: '100%',
        overflow: 'auto' as const,
        backgroundColor: theme.palette.primary.veryLight,
        flexGrow: 1,
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1),
        },
    },
    footer: {
        flexShrink: 0,
        padding: theme.spacing(2, 4),
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.primary.veryLight,
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1),
        },
    },
    heading: {
        flexShrink: 0, // Prevent header from shrinking
        height: theme.spacing(10),
        display: 'flex',
        justifyContent: 'space-between',
        paddingInline: theme.spacing(4),
        alignItems: 'center',
        borderBottom: `2px solid ${theme.palette.divider}`,
        zIndex: theme.zIndex.appBar, // Ensure modal header stays on top
        backgroundColor: theme.palette.primary.veryLight, // Match modal background
    },
});

type ModalProps = WithTranslation &
    KlpStyled & {
        isOpen: boolean;
        onClose: () => void;
        titleString?: string;
        className?: string;
        children: React.ReactNode;
        footer?: React.ReactNode;
    };

type KlpModalComponent = React.FC<ModalProps> & {
    Header: typeof KlpModalHeader;
    Content: typeof KlpModalContent;
    Footer: typeof KlpModalFooter;
};

export const KlpModal: KlpModalComponent = (props: ModalProps) => {
    const {
        isOpen,
        onClose,
        translation,
        translationOptions,
        titleString,
        children,
        className,
        footer,
        ...restProps
    } = props;
    const klpStyles = useKlpStyles(restProps);
    const classes = useKlpStyles({}, styles);

    // Extract different slots from children
    const childrenArray = React.Children.toArray(children);
    const headerSlot = childrenArray.find(
        (child) => React.isValidElement(child) && child.type === KlpModalHeader,
    );
    const contentSlot = childrenArray.find(
        (child) =>
            React.isValidElement(child) && child.type === KlpModalContent,
    );
    const footerSlot = childrenArray.find(
        (child) => React.isValidElement(child) && child.type === KlpModalFooter,
    );

    // Determine what content to show
    const hasSlots = Boolean(headerSlot ?? contentSlot ?? footerSlot);
    const mainContent = hasSlots ? contentSlot : children;

    return (
        <Modal open={isOpen} onClose={onClose}>
            <Box
                className={classNames(
                    classes.modal,
                    klpStyles.klpStyles,
                    className,
                )}
                {...removeKlpStyles(restProps)}
            >
                {/* Header: Custom slot or default header */}
                {headerSlot ?? (
                    <Box className={classes.heading}>
                        <Heading
                            klpHeadingLevel="THREE"
                            translation={translation}
                            translationOptions={translationOptions}
                        >
                            {titleString}
                        </Heading>
                        <IconButton
                            onClick={onClose}
                            data-testid="close-button"
                        >
                            <CloseIcon data-testid="icon-close" />
                        </IconButton>
                    </Box>
                )}

                {/* Content: Slot content or regular children */}
                <Box className={classes.content}>{mainContent}</Box>

                {/* Footer: Slot or prop */}
                {footerSlot && (
                    <Box className={classes.footer}>{footerSlot}</Box>
                )}
                {!footerSlot && footer && (
                    <Box className={classes.footer}>{footer}</Box>
                )}
            </Box>
        </Modal>
    );
};

// Attach the compound components
KlpModal.Header = KlpModalHeader;
KlpModal.Content = KlpModalContent;
KlpModal.Footer = KlpModalFooter;
